import classNames from "classnames"; // Keep this import
import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import EasyAPTLogo from "../../../assets/logos/EasyAPT.png";
import Button from "../../../components/bootstrap/Button";
import Card, { CardBody } from "../../../components/bootstrap/Card";
import Checks from "../../../components/bootstrap/forms/Checks";
import FormGroup from "../../../components/bootstrap/forms/FormGroup";
import Input from "../../../components/bootstrap/forms/Input";
import Spinner from "../../../components/bootstrap/Spinner";
import useDarkMode from "../../../hooks/useDarkMode";
import Page from "../../../layout/Page/Page";
import PageWrapper from "../../../layout/PageWrapper/PageWrapper";
import apiService from "../../../services/apiService";

const LoginHeader: FC = () => {
  return (
    <>
      <div className="text-center h1 fw-bold mt-5">Welcome to EasyApt</div>
      <div className="text-center h4 text-muted mb-5">Sign in to continue</div>
    </>
  );
};

interface ILoginProps {
  isSignUp?: boolean;
}
const Login: FC<ILoginProps> = () => {
  const { darkModeStatus } = useDarkMode();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      loginUsername: "", // This will be pre-populated from localStorage
      rememberMe: false,
    },
    validate: (values) => {
      const errors: { loginUsername?: string } = {};

      if (!values.loginUsername) {
        errors.loginUsername = "Required";
      } else if (!/^\d+$/.test(values.loginUsername)) {
        errors.loginUsername = "Phone number must contain only numbers";
      }

      return errors;
    },
    onSubmit: () => {},
  });
  
  // Effect to load saved phone number from localStorage on component mount
  useEffect(() => {
    const savedPhone = localStorage.getItem("easyapt_remembered_phone");
    if (savedPhone) {
      formik.setFieldValue("loginUsername", savedPhone);
      formik.setFieldValue("rememberMe", true);
    }
  }, []); // Run only once on mount

  // Effect to update localStorage when rememberMe changes
  useEffect(() => {
    if (!formik.values.rememberMe) {
      localStorage.removeItem("easyapt_remembered_phone");
    }
  }, [formik.values.rememberMe]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleContinue = async () => {
    setIsLoading(true);
    const phone = formik.values.loginUsername;

    if (!/^\d+$/.test(phone)) {
      formik.setFieldError(
        "loginUsername",
        "Phone number must contain only numbers.",
      );
      setIsLoading(false);
      return;
    }

    // Save or remove phone number based on "Remember Me" checkbox
    if (formik.values.rememberMe) {
      localStorage.setItem("easyapt_remembered_phone", phone);
    } else {
      localStorage.removeItem("easyapt_remembered_phone");
    }
    try {
      const response = await apiService.post(
        "/auth/send-otp",
        { phone },
        undefined,
      );

      if (response.ok) {
        navigate("/auth-pages/verify-otp", { state: { phone } });
      } else {
        formik.setFieldError(
          "loginUsername",
          "Failed to send OTP. Please try again.",
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login Error:", error);
      formik.setFieldError(
        "loginUsername",
        "Network error. Please check your connection.",
      );
      setIsLoading(false);
    }
  };

  return (
    <PageWrapper isProtected={false} title="Login" className="bg-dark">
      <Page className="p-0">
        <div className="row h-100 align-items-center justify-content-center">
          <div className="col-xl-4 col-lg-6 col-md-8 shadow-3d-container">
            <Card className="shadow-3d-dark" data-tour="login-page">
              <CardBody>
                <div className="text-center my-5">
                  <Link
                    to="/"
                    className={classNames(
                      "text-decoration-none  fw-bold display-2",
                      {
                        "text-dark": !darkModeStatus,
                        "text-light": darkModeStatus,
                      },
                    )}
                    aria-label="Facit"
                  >
                    <img src={EasyAPTLogo} alt="EasyAPT" width={200} />
                  </Link>
                </div>

                <LoginHeader />

                <form className="row g-4">
                  <div className="col-12">
                    <FormGroup
                      id="loginUsername"
                      isFloating
                      label="Phone Number with Country Code"
                    >
                      <Input
                        id="loginUsername"
                        name="loginUsername"
                        type="tel"
                        autoComplete="tel"
                        value={formik.values.loginUsername}
                        isTouched={formik.touched.loginUsername}
                        invalidFeedback={formik.errors.loginUsername}
                        isValid={formik.isValid && formik.touched.loginUsername}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        onFocus={() => {
                          formik.setErrors({});
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-12">
                    <Checks
                      id="rememberMe"
                      name="rememberMe"
                      label="Remember me"
                      type="checkbox"
                      value="rememberMe"
                      checked={formik.values.rememberMe}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isValid={formik.touched.rememberMe && !formik.errors.rememberMe}
                      invalidFeedback={formik.errors.rememberMe}
                    />
                  </div>
                  <div className="col-12">
                    <Button
                      color="warning"
                      className="w-100 py-3"
                      isDisable={!formik.values.loginUsername}
                      onClick={handleContinue}
                    >
                      {isLoading && <Spinner isSmall inButton isGrow />}
                      Request for OTP
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
            <div className="text-center">
              <a href="/" className="text-decoration-none me-3 link-dark">
                Privacy policy
              </a>
              <a href="/" className="link-light text-decoration-none link-dark">
                Terms of use
              </a>
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
};

export default Login;
